import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getApiUrl } from '../config/api';
import '../styles/RatingDisplay.css';

interface Rating {
    _id: string;
    overallRating: number;
    criteria: {
        professionalism?: number;
        skill?: number;
        punctuality?: number;
        communication?: number;
        payment?: number;
        jobAccuracy?: number;
    };
    review?: string;
    ratedBy: {
        _id: string;
        fullName: string;
        userType: string;
    };
    engagement?: {
        _id: string;
        jobRole: string;
        date: string;
        place: string;
        facilityName?: string;
    };
    createdAt: string;
}

interface RatingSummary {
    averageRating: number;
    totalRatings: number;
    ratingBreakdown: {
        5: number;
        4: number;
        3: number;
        2: number;
        1: number;
    };
    criteriaAverages?: {
        professionalism?: number;
        skill?: number;
        punctuality?: number;
        communication?: number;
        payment?: number;
        jobAccuracy?: number;
    };
}

interface RatingDisplayProps {
    userId: string;
    isOwnProfile?: boolean;
    showSummary?: boolean;
    autoShowReviews?: boolean;
    hideToggleButton?: boolean;
}

const RatingDisplay: React.FC<RatingDisplayProps> = ({ userId, isOwnProfile = false, showSummary = true, autoShowReviews = false, hideToggleButton = false }) => {
    const { t } = useTranslation();
    const [ratings, setRatings] = useState<Rating[]>([]);
    const [summary, setSummary] = useState<RatingSummary | null>(null);
    const [loading, setLoading] = useState(true);
    const [showAll, setShowAll] = useState(false);
    const [showReviews, setShowReviews] = useState(autoShowReviews);

    useEffect(() => {
        fetchRatings();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userId]);

    const fetchRatings = async () => {
        setLoading(true);
        try {
            const response = await fetch(
                getApiUrl(`ratings/user/${userId}?limit=5`)
            );

            if (!response.ok) {
                throw new Error('Failed to fetch ratings');
            }

            const data = await response.json();
            setRatings(data.ratings || []);
            setSummary(data.summary || null);
        } catch (error) {
            console.error('Error fetching ratings:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchAllRatings = async () => {
        try {
            const response = await fetch(
                getApiUrl(`ratings/user/${userId}?limit=100`)
            );

            if (!response.ok) {
                throw new Error('Failed to fetch ratings');
            }

            const data = await response.json();
            setRatings(data.ratings || []);
            setShowAll(true);
        } catch (error) {
            console.error('Error fetching all ratings:', error);
        }
    };

    const renderStars = (rating: number) => {
        const fullStars = Math.floor(rating);
        const hasHalfStar = rating % 1 >= 0.5;
        const emptyStars = 5 - fullStars - (hasHalfStar ? 1 : 0);

        return (
            <div className="stars-display">
                {'★'.repeat(fullStars)}
                {hasHalfStar && '⯨'}
                {'☆'.repeat(emptyStars)}
            </div>
        );
    };

    const renderBreakdown = () => {
        if (!summary) return null;

        return (
            <div className="rating-breakdown">
                {[5, 4, 3, 2, 1].map((star) => {
                    const count = summary.ratingBreakdown[star as keyof typeof summary.ratingBreakdown] || 0;
                    const percentage = summary.totalRatings > 0 
                        ? (count / summary.totalRatings) * 100 
                        : 0;

                    return (
                        <div key={star} className="breakdown-row">
                            <span className="breakdown-label">{star} ★</span>
                            <div className="breakdown-bar">
                                <div 
                                    className="breakdown-fill" 
                                    style={{ width: `${percentage}%` }}
                                />
                            </div>
                            <span className="breakdown-count">{count}</span>
                        </div>
                    );
                })}
            </div>
        );
    };

    if (loading) {
        return <div className="rating-display loading">{t('ratingDisplay.loading')}</div>;
    }

    const translateUserType = (userType: string): string => {
        if (!userType) return '';
        const normalized = userType.toLowerCase().replace(/[\s_-]/g, '_').replace(/\//g, '');
        const translated = t(`userTypes.${normalized}`);
        // If translation key doesn't have a match, try the original
        if (translated === `userTypes.${normalized}`) {
            return userType;
        }
        return translated;
    };

    if (!summary || summary.totalRatings === 0) {
        return (
            <div className="rating-display no-ratings">
                <p>{t('ratingDisplay.noRatings')}</p>
            </div>
        );
    }

    return (
        <div className="rating-display">
            {showSummary && (
                <div className="rating-summary">
                    <div className="summary-main">
                        <div className="average-rating">
                            <span className="rating-number">{(summary.averageRating || 0).toFixed(1)}</span>
                            {renderStars(summary.averageRating || 0)}
                        </div>
                        <p className="rating-count">
                            {summary.totalRatings}{' '}
                            {summary.totalRatings === 1 ? t('ratingDisplay.review') : t('ratingDisplay.reviews')}
                        </p>
                    </div>
                    {renderBreakdown()}
                </div>
            )}

            <div className="ratings-list">
                {!hideToggleButton && (
                    <div className="reviews-header">
                        <h4>{t('ratingDisplay.reviewsTitle', { count: summary.totalRatings })}</h4>
                        <button 
                            className="btn-toggle-reviews" 
                            onClick={() => setShowReviews(!showReviews)}
                        >
                            {showReviews ? t('ratingDisplay.hideReviews') : t('ratingDisplay.showReviews')}
                        </button>
                    </div>
                )}
                
                {showReviews && (
                    <>
                        {ratings.map((rating) => (
                            <div key={rating._id} className="rating-card">
                                <div className="rating-header">
                                    <div className="rating-author">
                                        <strong>{rating.ratedBy.fullName}</strong>
                                        <span className="user-type">({translateUserType(rating.ratedBy.userType)})</span>
                                    </div>
                                    <div className="rating-score">
                                        {rating.overallRating} ★
                                    </div>
                                </div>
                                
                                {rating.engagement && (
                                    <div className="rating-engagement-info">
                                        <small>
                                            {t(`roles.${rating.engagement.jobRole.toLowerCase().replace(/\s+/g, '').replace('/', '').replace('&', '')}`) || rating.engagement.jobRole} • {new Date(rating.engagement.date).toLocaleDateString()} • {rating.engagement.facilityName || rating.engagement.place}
                                        </small>
                                    </div>
                                )}

                                {rating.criteria && Object.keys(rating.criteria).length > 0 && (
                                    <div className="rating-criteria">
                                        {Object.entries(rating.criteria)
                                            .filter(([key]) => key !== '_id' && key !== 'id')
                                            .map(([key, value]) => (
                                                value && (
                                                    <span key={key} className="criteria-badge">
                                                        {t(`ratingCriteria.${key}`) || key}: {value}★
                                                    </span>
                                                )
                                            ))}
                                    </div>
                                )}

                                {rating.review && (
                                    <p className="rating-review">{rating.review}</p>
                                )}

                                <div className="rating-date">
                                    <small>{new Date(rating.createdAt).toLocaleDateString()}</small>
                                </div>
                            </div>
                        ))}

                        {!showAll && ratings.length >= 5 && summary.totalRatings > 5 && (
                            <button 
                                className="btn-show-more" 
                                onClick={fetchAllRatings}
                            >
                                {t('ratingDisplay.showAllReviews', { count: summary.totalRatings })}
                            </button>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

export default RatingDisplay;
